import type { AppEnvironment } from './types';
import { IP4CMS_PAGE_COPY } from './page-copy-ip4cms';

/**
 * IP4CMS member portal — STAGING. Served from `dist/ip4cms-staging` on
 * `*.member.ip4cms-staging.ip4x.systems`. Points at the ip4cms staging stack
 * (core-api / api .ip4cms-staging.ip4x.systems). Per-tenant branding/config is
 * resolved at runtime from the hostname via Core resolve-domain.
 *
 * Core (the ip4cms vertical core-api, :3000) must include
 * `member.ip4cms-staging.ip4x.systems` in BASE_DOMAINS so the `{slug}` is
 * extracted and on-demand TLS is approved for tenants that exist.
 *
 * SSO build: scoped to root tenant e50b733a, root portal served at
 * ip4cmsadmin.member.ip4cms-staging.ip4x.systems. Also the basis for the native
 * SSO app (defaultDomain resolves the root when there's no browser hostname).
 */
export const env: AppEnvironment = {
  production: true,
  buildMode: 'ip4cms-staging',
  apiBaseUrl: 'https://api.ip4cms-staging.ip4x.systems/v1',
  coreApiUrl: 'https://core-api.ip4cms-staging.ip4x.systems/v1',
  appTemplateId: '',
  authMode: 'core',
  portalOwnerType: 'member',
  loadDomainConfig: true,
  // SSO via root tenant: scope is the e50b733a root; community switching among its
  // children. defaultDomain pins the root portal host (used by the native app where
  // there's no browser hostname, and as the web fallback).
  defaultDomain: 'ip4cmsadmin.member.ip4cms-staging.ip4x.systems',
  rootTenantId: 'e50b733a-f564-429d-8885-12dc9f6d7cd9',
  portalBaseUrl: 'https://ip4cmsadmin.member.ip4cms-staging.ip4x.systems',
  appName: 'IP4CMS',
  version: '1.0.0',
  appShell: 'classic',
  keyboardShift: true,
  welcomeContent: {
    eyebrow: 'Member portal',
    title: 'Your community,\nin your pocket.',
    subtitle: 'Access and services in one place.',
  },
  communitySelectContent: {
    title: 'Choose your community',
    subtitle: 'Search for your community to continue.',
  },
  joinCommunity: {
    enabled: true,
    entryLabel: 'Join a community',
    entityName: 'community',
    entityPlural: 'communities',
    title: 'Join a community',
    subtitle: 'Request access to another community.',
    searchTitle: 'Find your community',
    searchSubtitle: 'Search for the community you want to join.',
    searchPlaceholder: 'Search communities…',
    detailsTitle: 'Your details',
    detailsSubtitle: 'Confirm your details.',
    submitLabel: 'Request to join',
    termsLabel: 'I accept the terms and conditions',
    successTitle: 'Request submitted',
    successMessage:
      "Your request to join has been sent. You'll be notified once the community reviews it.",
    pendingTitle: 'Request pending',
    pendingMessage: 'You already have a pending request to join this community.',
  },
  signupTermsUrl: 'https://www.glovent.co.za/terms',
  pageCopy: IP4CMS_PAGE_COPY,
  home: {
    widgetMode: true,
    widgets: [
      { id: 'support', type: 'support', title: 'Helpdesk', order: 1, enabled: true, requiresModule: 'support' },
      { id: 'events', type: 'events', title: 'Upcoming events', order: 2, enabled: true, requiresModule: 'events' },
      { id: 'panic', type: 'panic', title: 'Emergency', order: 3, enabled: true, requiresModule: 'panic' },
      { id: 'utilities', type: 'utilities', title: 'Utilities', order: 4, enabled: true, requiresModule: 'utilities' },
    ],
  },
};
